import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchAssetTemplateOccurrence, fetchAssetTemplateOccurrences } from '../actions';

/**
 * Keeps a live handle on what is open and reloads it after a write.
 *
 * The refs exist because these values are read inside async API callbacks,
 * where the captured render-time value is already stale.
 */
export function useAssetViewRefresh({
  selectedTemplateId,
  selectedOccurrenceId,
  selectedSidebarItem,
  onSelectTemplate,
}: {
  selectedTemplateId: number | null;
  selectedOccurrenceId: number | null;
  selectedSidebarItem: any;
  onSelectTemplate: (templateId: number) => void;
}) {
  const dispatch = useDispatch();

  const selectedTemplateIdRef = useRef(selectedTemplateId);
  const selectedOccurrenceIdRef = useRef<number | null>(selectedOccurrenceId ?? null);
  const selectedSidebarItemRef = useRef(selectedSidebarItem);

  useEffect(() => {
    selectedTemplateIdRef.current = selectedTemplateId;
  }, [selectedTemplateId]);

  useEffect(() => {
    selectedOccurrenceIdRef.current = selectedOccurrenceId ?? null;
  }, [selectedOccurrenceId]);

  useEffect(() => {
    selectedSidebarItemRef.current = selectedSidebarItem;
  }, [selectedSidebarItem]);

  const refreshOpenAssetView = () => {
    if (selectedOccurrenceIdRef.current) {
      dispatch(fetchAssetTemplateOccurrence(selectedOccurrenceIdRef.current));
      dispatch(fetchAssetTemplateOccurrences());
      return;
    }
    if (selectedTemplateIdRef.current) onSelectTemplate(selectedTemplateIdRef.current);
  };

  useEffect(() => {
    if (selectedOccurrenceIdRef.current) {
      dispatch(fetchAssetTemplateOccurrence(selectedOccurrenceIdRef.current));
    } else if (selectedTemplateIdRef.current) {
      onSelectTemplate(selectedTemplateIdRef.current);
    }
  }, []);

  return {
    selectedTemplateIdRef,
    selectedOccurrenceIdRef,
    selectedSidebarItemRef,
    refreshOpenAssetView,
  };
}
